/**
 * Listing End Date utilities.
 *
 * Listings can have an expiry date stored in publicData.listingEndDate (ISO string).
 * When the date passes, a cron job closes the listing automatically.
 *
 * Status calculations:
 *   - 'active'   — end date in the future
 *   - 'expiring' — end date within EXPIRING_SOON_DAYS days
 *   - 'expired'  — end date in the past
 */

export const EXPIRING_SOON_DAYS = 3;

/**
 * Get the status of a listing end date.
 * @param {string|null} endDate — ISO date string from publicData.listingEndDate
 * @returns {'active'|'expiring'|'expired'|null} — null if no end date
 */
export const getEndDateStatus = (endDate) => {
  if (!endDate) return null;
  const now = Date.now();
  const end = new Date(endDate).getTime();

  if (end < now) return 'expired';

  const daysUntil = (end - now) / (1000 * 60 * 60 * 24);
  if (daysUntil <= EXPIRING_SOON_DAYS) return 'expiring';

  return 'active';
};

/**
 * Format time remaining until expiry.
 * @param {string} endDate — ISO date string
 * @returns {string} — e.g. '3 days', '5 hours', 'Expired'
 */
export const formatTimeUntilExpiry = (endDate) => {
  if (!endDate) return '';
  const ms = new Date(endDate).getTime() - Date.now();
  if (ms <= 0) return 'Expired';

  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  if (days > 0) return `${days} day${days === 1 ? '' : 's'}`;

  const hours = Math.floor(ms / (1000 * 60 * 60));
  if (hours > 0) return `${hours} hour${hours === 1 ? '' : 's'}`;

  const minutes = Math.floor(ms / (1000 * 60));
  return `${minutes} minute${minutes === 1 ? '' : 's'}`;
};

/**
 * Check if a listing is expired.
 * @param {string|null} endDate
 * @returns {boolean}
 */
export const isExpired = (endDate) =>
  !!endDate && new Date(endDate).getTime() < Date.now();
